import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Truck, PackageCheck, Upload, Check, ExternalLink, X, Mail, ChevronDown, ChevronRight, Layers, Pencil } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  getLogisticsQueue, dispatchItem, getFirms,
  getDeliveryPersons, createDeliveryPerson, deleteDeliveryPerson,
  markLotDelivered, resendLotEmail, resendItemEmail, markDelivered,
  dispatchByInvoice, deliverByInvoice, adminRevertItem, adminUpdateInvoice,
} from '../../services/taskApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailToggle = ({ checked, onChange, isDark, testId }) => (
  <label className={`flex items-center gap-2.5 text-xs cursor-pointer select-none rounded-lg border px-3 py-2.5 ${isDark ? 'border-white/15 bg-white/[0.04] text-white/80' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
    <input type="checkbox" className="w-4 h-4 accent-pink-600" checked={checked} onChange={e => onChange(e.target.checked)} data-testid={testId} />
    <Mail size={13} className={checked ? 'text-pink-500' : 'opacity-40'} />
    <span className="font-medium">Send email notification to customer</span>
  </label>
);

const STATUS_TABS = [
  { key: 'Awaiting Dispatch', label: 'Awaiting Dispatch', color: 'bg-pink-500/15 text-pink-400' },
  { key: 'Dispatched', label: 'In Transit', color: 'bg-blue-500/15 text-blue-400' },
  { key: 'Delivered', label: 'Delivered', color: 'bg-green-500/15 text-green-400' },
];

const fileLink = (url) => url?.startsWith('http') ? url : `${API_URL}${url?.startsWith('/api/') ? '' : '/api/files/'}${url || ''}`;

const FlowLogistics = () => {
  const { isDark, user } = useOutletContext();
  const [items, setItems] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState('');
  const [tab, setTab] = useState('Awaiting Dispatch');
  const [dispatchItem_, setDispatchItem_] = useState(null);
  const [deliverLot, setDeliverLot] = useState(null);   // { item, lot }
  const [submitting, setSubmitting] = useState(false);
  const [dForm, setDForm] = useState({ quantity: 0, dispatch_mode: 'courier', dispatch_person: '', courier_name: '', awb_no: '', vehicle_no: '', expected_delivery: '', send_email: true });
  const [pForm, setPForm] = useState({ pod_url: '', receiver_name: '', send_email: true });
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [newPersonForm, setNewPersonForm] = useState({ name: '', phone: '', vehicle: '' });
  const [expanded, setExpanded] = useState({});  // item_id -> bool
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 50;

  // Auto-expand items in Delivered / Dispatched tabs that have 2+ lots so all PODs
  // are visible without an extra click. Fixes: "if one SO has multiple bills, only
  // the latest POD is showing" — actually all PODs exist as lots, but they were
  // hidden under the collapsed row.
  useEffect(() => {
    if (tab !== 'Delivered' && tab !== 'Dispatched') return;
    setExpanded(prev => {
      const next = { ...prev };
      let dirty = false;
      for (const it of items) {
        const lotCount = (it.lots || []).length;
        if (lotCount >= 2 && next[it.id] === undefined) {
          next[it.id] = true;
          dirty = true;
        }
      }
      return dirty ? next : prev;
    });
  }, [items, tab]);
  // Bill-wise dispatch
  const [bulkInvoice, setBulkInvoice] = useState(null);   // { invoice, items }
  const [bulkForm, setBulkForm] = useState({ dispatch_mode: 'courier', dispatch_person: '', courier_name: '', awb_no: '', vehicle_no: '', expected_delivery: '', send_email: true });
  // Bill-wise delivery (POD-based)
  const [bulkDeliver, setBulkDeliver] = useState(null);   // { invoice, items }
  const [bulkDeliverForm, setBulkDeliverForm] = useState({ pod_url: '', receiver_name: '', send_email: true });
  // Admin invoice correction
  const [editInv, setEditInv] = useState(null); // { old_no, new_no, invoice_date, invoice_pdf_url }

  const cardCls = isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-gray-200';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white dark-select' : 'bg-white border-gray-300 text-gray-900';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-white border-gray-300';

  const fetchData = useCallback(async (pageArg = 1) => {
    if (pageArg === 1) setLoading(true); else setLoadingMore(true);
    try {
      const params = { status: tab, page: pageArg, limit: PAGE_SIZE };
      if (filterCompany) params.company_id = filterCompany;
      if (search) params.q = search;
      const [iRes, fRes, dpRes] = await Promise.all([getLogisticsQueue(params), getFirms(), getDeliveryPersons()]);
      const data = iRes.data || {};
      setItems(prev => pageArg === 1 ? (data.items || []) : [...prev, ...(data.items || [])]);
      setTotal(data.total || 0);
      setPage(pageArg);
      setFirms(fRes.data || []);
      setDeliveryPersons(dpRes.data || []);
    } catch { /* swallow */ }
    finally { setLoading(false); setLoadingMore(false); }
  }, [filterCompany, tab, search]);

  useEffect(() => { fetchData(1); }, [fetchData]);

  const openDispatch = (item) => {
    const pending = (item.quantity_pending_dispatch !== undefined)
      ? item.quantity_pending_dispatch
      : (item.quantity_billed || item.quantity_allocated_to_bill || 0);
    setDispatchItem_({ ...item, _pending: pending });
    setDForm({ quantity: pending, dispatch_mode: 'courier', dispatch_person: '', courier_name: '', awb_no: '', vehicle_no: '', expected_delivery: '', send_email: true });
  };

  const handleUpload = async (e, setter, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = localStorage.getItem('task_token');
      const res = await fetch(`${API_URL}/api/tasks-app/upload`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      const data = await res.json();
      if (data.url) {
        setter(f => ({ ...f, [field]: data.url }));
        toast.success('Uploaded');
      }
    } catch { toast.error('Upload failed'); }
  };

  const handleDispatch = async (e) => {
    e.preventDefault();
    const pending = dispatchItem_._pending || 0;
    if (pending < 1) { toast.error('Nothing pending to dispatch'); return; }
    if (dForm.dispatch_mode === 'courier' && (!dForm.courier_name.trim() || !dForm.awb_no.trim())) {
      toast.error('Courier name and tracking number are required'); return;
    }
    if ((dForm.dispatch_mode === 'own' || dForm.dispatch_mode === 'delivery_person') && !dForm.dispatch_person.trim()) {
      toast.error('Person name required'); return;
    }
    setSubmitting(true);
    try {
      // Quantity is fixed = pending (= what billing released). No user override.
      const res = await dispatchItem(dispatchItem_.id, { ...dForm, quantity: pending });
      toast.success(res.data.message || 'Dispatched & customer notified');
      setDispatchItem_(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleAddPerson = async () => {
    if (!newPersonForm.name.trim()) { toast.error('Name required'); return; }
    try {
      const res = await createDeliveryPerson(newPersonForm);
      toast.success('Person added');
      setDeliveryPersons(p => [...p, res.data]);
      setDForm(f => ({ ...f, dispatch_person: res.data.name }));
      setShowAddPerson(false);
      setNewPersonForm({ name: '', phone: '', vehicle: '' });
    } catch { toast.error('Failed'); }
  };

  const handleDeletePerson = async (p) => {
    if (!window.confirm(`Delete delivery person "${p.name}"?`)) return;
    try {
      await deleteDeliveryPerson(p.id);
      toast.success('Deleted');
      setDeliveryPersons(list => list.filter(x => x.id !== p.id));
      if (dForm.dispatch_person === p.name) setDForm(f => ({ ...f, dispatch_person: '' }));
    } catch { toast.error('Failed'); }
  };

  const handleDeliverLot = async (e) => {
    e.preventDefault();
    if (!pForm.pod_url) { toast.error('Upload POD image'); return; }
    setSubmitting(true);
    try {
      if (deliverLot.lot) {
        await markLotDelivered(deliverLot.lot.id, pForm);
      } else {
        // legacy item without lots
        await markDelivered(deliverLot.item.id, pForm);
      }
      toast.success('Delivery confirmed & customer notified');
      setDeliverLot(null);
      setPForm({ pod_url: '', receiver_name: '', send_email: true });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleResendLot = async (lotId) => {
    try {
      const r = await resendLotEmail(lotId);
      toast.success(`Email resent (${r.data.resent})`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleResendItem = async (itemId) => {
    try {
      const r = await resendItemEmail(itemId);
      toast.success(`Email resent (${r.data.resent})`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));
  const getFirmName = (id) => firms.find(f => f.id === id)?.name || id;

  // Group items by Tally invoice so Logistics can dispatch the whole bill in one go
  const invoiceGroups = useMemo(() => {
    if (tab !== 'Awaiting Dispatch') return [];
    const m = {};
    for (const it of items) {
      const k = (it.tally_invoice_no || '').trim();
      if (!k) continue; // bill-wise needs an invoice
      const pending = it.quantity_pending_dispatch ?? ((it.quantity_billed || it.quantity_allocated_to_bill || 0) - (it.quantity_dispatched || 0));
      if (pending <= 0) continue;
      if (!m[k]) m[k] = { invoice: k, items: [], customer: it.order_info?.customer_name || '' };
      m[k].items.push({ ...it, _pending: pending });
    }
    // Show only invoices that have 2+ items pending — single-item invoices already have their own button
    return Object.values(m).filter(g => g.items.length >= 2);
  }, [items, tab]);

  // Group still-in-transit items by Tally invoice so Logistics can deliver the whole bill in one go.
  // We list every in-transit invoice — single-item ones get a card too, so the screen has one consistent look.
  const deliveryGroups = useMemo(() => {
    if (tab !== 'Dispatched') return [];
    const m = {};
    for (const it of items) {
      const k = (it.tally_invoice_no || '').trim();
      // collect lots still in transit
      const inTransitLots = (it.lots || []).filter(l => l.status === 'Dispatched');
      if (inTransitLots.length === 0) continue;
      // group by invoice; items without a tally invoice fall into a "__no_inv__<itemId>" bucket so each still gets its own card
      const key = k || `__no_inv__${it.id}`;
      if (!m[key]) m[key] = { invoice: k, items: [], customer: it.order_info?.customer_name || '', totalLots: 0 };
      m[key].items.push({ ...it, _lots: inTransitLots });
      m[key].totalLots += inTransitLots.length;
    }
    return Object.values(m);
  }, [items, tab]);

  const handleRevert = async (item) => {
    const reason = window.prompt(
      `↩ Revert "${item.item_name}" one step backwards?\n\n` +
      `Current stage: ${item.fulfillment_status}\n` +
      `Any dispatch lots for this item will be voided (delivered lots block the revert).\n\n` +
      `Enter reason (audit trail, min 5 chars):`,
      ''
    );
    if (reason === null) return;
    if (!reason.trim() || reason.trim().length < 5) { toast.error('Reason must be at least 5 characters'); return; }
    try {
      const res = await adminRevertItem(item.id, reason.trim());
      toast.success(res.data.message || 'Item reverted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Revert failed'); }
  };

  const openBulkDispatch = (grp) => {
    setBulkInvoice(grp);
    setBulkForm({ dispatch_mode: 'courier', dispatch_person: '', courier_name: '', awb_no: '', vehicle_no: '', expected_delivery: '', send_email: true });
  };

  const handleBulkDispatch = async (e) => {
    e.preventDefault();
    if (!bulkInvoice) return;
    if (bulkForm.dispatch_mode === 'courier' && (!bulkForm.courier_name.trim() || !bulkForm.awb_no.trim())) {
      toast.error('Courier name and tracking number are required'); return;
    }
    if ((bulkForm.dispatch_mode === 'own' || bulkForm.dispatch_mode === 'delivery_person') && !bulkForm.dispatch_person.trim()) {
      toast.error('Person name required'); return;
    }
    setSubmitting(true);
    try {
      const res = await dispatchByInvoice({ tally_invoice_no: bulkInvoice.invoice, ...bulkForm });
      toast.success(res.data.message || 'Invoice dispatched');
      setBulkInvoice(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk dispatch failed'); }
    finally { setSubmitting(false); }
  };

  const openBulkDeliver = (grp) => {
    setBulkDeliver(grp);
    setBulkDeliverForm({ pod_url: '', receiver_name: '', send_email: true });
  };

  const openEditInvoice = (invNo) => setEditInv({ old_no: invNo || '', new_no: invNo || '', invoice_date: '', invoice_pdf_url: '' });

  const handleEditInvoice = async (e) => {
    e.preventDefault();
    if (!editInv?.old_no) return;
    const payload = { old_invoice_no: editInv.old_no };
    if (editInv.new_no && editInv.new_no.trim() !== editInv.old_no) payload.new_invoice_no = editInv.new_no.trim();
    if (editInv.invoice_date) payload.invoice_date = editInv.invoice_date;
    if (editInv.invoice_pdf_url) payload.invoice_pdf_url = editInv.invoice_pdf_url;
    if (!payload.new_invoice_no && !payload.invoice_date && !payload.invoice_pdf_url) {
      toast.error('Change the invoice number, date or upload a corrected PDF first'); return;
    }
    setSubmitting(true);
    try {
      const res = await adminUpdateInvoice(payload);
      toast.success(res.data.message || 'Invoice corrected');
      setEditInv(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Correction failed'); }
    finally { setSubmitting(false); }
  };

  const handleBulkDeliver = async (e) => {
    e.preventDefault();
    if (!bulkDeliver) return;
    if (!bulkDeliverForm.pod_url) { toast.error('Upload POD image'); return; }
    setSubmitting(true);
    try {
      const res = await deliverByInvoice({ tally_invoice_no: bulkDeliver.invoice, ...bulkDeliverForm });
      toast.success(res.data.message || 'Invoice delivered & customer notified');
      setBulkDeliver(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk delivery failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4" data-testid="flow-logistics">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Logistics & Delivery</h1>
          <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Split-shipment dispatch tracking & proof of delivery</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input placeholder="Search SO #, item, invoice #, customer..." value={search} onChange={e => setSearch(e.target.value)}
            className={`h-9 w-64 text-xs ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : ''}`} data-testid="logistics-search-input" />
          <select className={`h-9 rounded-lg text-xs px-3 border ${selectCls}`} value={filterCompany} onChange={e => setFilterCompany(e.target.value)}>
            <option value="">All Companies</option>
            {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 p-1 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
        {STATUS_TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key.replace(/\s/g, '-').toLowerCase()}`}
            className={`flex-1 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
              tab === t.key
                ? isDark ? 'bg-white/10 text-white shadow' : 'bg-white text-gray-900 shadow-sm'
                : isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className={`h-16 rounded-xl animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div>
      ) : items.length === 0 ? (
        <Card className={cardCls}><CardContent className="p-8 text-center">
          <Truck size={40} className={`mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-white/40' : 'text-gray-500'}>No items in this stage</p>
        </CardContent></Card>
      ) : (
        <>
        {invoiceGroups.length > 0 && (
          <Card className={`${cardCls} border-pink-500/30`} data-testid="bill-wise-dispatch-card">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={14} className="text-pink-400" />
                <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Bill-wise dispatch</h3>
                <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{invoiceGroups.length} invoice{invoiceGroups.length > 1 ? 's' : ''} with multiple items</span>
              </div>
              <div className="space-y-2">
                {invoiceGroups.map(grp => {
                  const total = grp.items.reduce((s, it) => s + (it._pending || 0), 0);
                  return (
                    <div key={grp.invoice} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-200'}`}>
                      <div className="min-w-0 text-xs">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{grp.invoice}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${isDark ? 'bg-pink-500/15 text-pink-300' : 'bg-pink-50 text-pink-700'}`}>{grp.items.length} items · {total} units</span>
                        </div>
                        <div className={`text-[10px] mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Customer: <b>{grp.customer || '-'}</b></div>
                      </div>
                      <Button size="sm" className="bg-pink-600 hover:bg-pink-700 h-8 px-4 text-xs" onClick={() => openBulkDispatch(grp)} data-testid={`bulk-dispatch-${grp.invoice}`}>
                        <Truck size={12} className="mr-1.5" /> Dispatch entire invoice
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
        {deliveryGroups.length > 0 && (
          <Card className={`${cardCls} border-green-500/30`} data-testid="bill-wise-deliver-card">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={14} className="text-green-400" />
                <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Bill-wise delivery</h3>
                <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{deliveryGroups.length} invoice{deliveryGroups.length > 1 ? 's' : ''} in transit · one POD covers all items</span>
              </div>
              <div className="space-y-2">
                {deliveryGroups.map(grp => {
                  const itemNames = grp.items.map(it => it.item_name).join(', ');
                  return (
                  <div key={grp.invoice || `noinv-${grp.items[0]?.id}`} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-200'}`}>
                    <div className="min-w-0 text-xs flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{grp.invoice || '— (no invoice)'}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${isDark ? 'bg-green-500/15 text-green-300' : 'bg-green-50 text-green-700'}`}>{grp.items.length} item{grp.items.length > 1 ? 's' : ''} · {grp.totalLots} lot{grp.totalLots > 1 ? 's' : ''}</span>
                      </div>
                      <div className={`text-[10px] mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                        Customer: <b>{grp.customer || '-'}</b>
                        <span className="mx-1.5">·</span>
                        SO: <b>{Array.from(new Set(grp.items.map(it => it.so_number))).join(', ')}</b>
                      </div>
                      <div className={`text-[10px] mt-0.5 truncate ${isDark ? 'text-white/40' : 'text-gray-400'}`} title={itemNames}>
                        {itemNames}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 h-9 px-4 text-xs w-full sm:w-auto"
                      onClick={() => openBulkDeliver(grp)}
                      data-testid={`bulk-deliver-${grp.invoice || `noinv-${grp.items[0]?.id}`}`}
                      disabled={!grp.invoice}
                      title={!grp.invoice ? 'Bill-wise delivery needs a Tally invoice number — use the per-item action below' : 'Mark all items of this invoice delivered with one POD'}
                    >
                      <PackageCheck size={12} className="mr-1.5" /> Deliver entire invoice
                    </Button>
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
        {tab !== 'Dispatched' && (
        <Card className={cardCls}>
          <CardContent className="p-2 sm:p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[800px]">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <th className="w-6"></th>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>SO #</th>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Customer</th>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Item</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Qty Progress</th>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Invoice</th>
                    <th className={`text-right py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const lots = item.lots || [];
                    const billed = item.quantity_billed || item.quantity_allocated_to_bill || 0;
                    const dispatched = item.quantity_dispatched || 0;
                    const delivered = item.quantity_delivered || 0;
                    const pending = item.quantity_pending_dispatch ?? Math.max(billed - dispatched, 0);
                    const isExp = expanded[item.id];
                    return (
                      <React.Fragment key={item.id}>
                        <tr className={`border-b ${isDark ? 'border-white/5 hover:bg-white/[0.02]' : 'border-gray-50 hover:bg-gray-50'} transition-colors`}>
                          <td className="px-1">
                            {lots.length > 0 && (
                              <button onClick={() => toggleExpand(item.id)} className={isDark ? 'text-white/40 hover:text-white' : 'text-gray-400 hover:text-gray-700'} data-testid={`expand-${item.id}`}>
                                {isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </button>
                            )}
                          </td>
                          <td className={`py-3 px-2 font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{item.so_number}<div className={`text-[9px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{getFirmName(item.order_info?.company_id)}</div></td>
                          <td className={`py-3 px-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{item.order_info?.customer_name}</td>
                          <td className={`py-3 px-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {item.item_name}
                            {item.purchase_item_name && <div className="text-[9px] text-purple-400" title="Purchase team's item name">Purch: {item.purchase_item_name}</div>}
                          </td>
                          <td className="py-3 px-2 text-center">
                            <div className={`text-[11px] font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{billed}</div>
                            <div className={`text-[10px] ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                              <span className="text-blue-400">{dispatched}</span> shipped · <span className="text-green-400">{delivered}</span> delivered
                              {pending > 0 && <> · <span className="text-pink-400">{pending} pending</span></>}
                            </div>
                            {billed > 0 && (
                              <div className={`mt-1 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                                <div className="h-full bg-gradient-to-r from-blue-500 to-green-500" style={{ width: `${Math.min(100, ((delivered + (dispatched - delivered) * 0.5) / billed) * 100)}%` }} />
                              </div>
                            )}
                          </td>
                          <td className={`py-3 px-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                            {/* Multi-bill display: show ALL invoices from history (Cycle 6 #1).
                                If item was billed under multiple invoice numbers, list each one on its
                                own line with a link to that invoice's PDF. */}
                            {Array.isArray(item.invoice_history) && item.invoice_history.length > 0 ? (
                              <div className="space-y-0.5" data-testid={`invoice-history-${item.id}`}>
                                {item.invoice_history.map((inv, idx) => (
                                  <div key={`${inv.tally_invoice_no || 'no-no'}-${idx}`} className="flex items-center gap-1 text-xs">
                                    <span className={idx === item.invoice_history.length - 1 ? (isDark ? 'text-white/90 font-medium' : 'text-gray-900 font-medium') : ''}>
                                      {inv.tally_invoice_no || '-'}
                                    </span>
                                    {inv.billed_qty && (
                                      <span className={`text-[9px] px-1 rounded ${isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'}`}>
                                        {inv.billed_qty}u
                                      </span>
                                    )}
                                    {inv.invoice_pdf_url && (
                                      <a href={fileLink(inv.invoice_pdf_url)} target="_blank" rel="noreferrer" className="text-blue-400" title={`View invoice ${inv.tally_invoice_no}`}>
                                        <ExternalLink size={10} className="inline" />
                                      </a>
                                    )}
                                    {user?.role === 'admin' && inv.tally_invoice_no && (
                                      <button onClick={() => openEditInvoice(inv.tally_invoice_no)} className="text-orange-400 hover:text-orange-300" title="Correct this invoice (admin)" data-testid={`edit-invoice-${item.id}-${idx}`}>
                                        <Pencil size={10} className="inline" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <>
                                {item.tally_invoice_no || '-'}
                                {item.invoice_pdf_url && (
                                  <a href={fileLink(item.invoice_pdf_url)} target="_blank" rel="noreferrer" className="ml-2 text-blue-400">
                                    <ExternalLink size={11} className="inline" />
                                  </a>
                                )}
                                {user?.role === 'admin' && item.tally_invoice_no && (
                                  <button onClick={() => openEditInvoice(item.tally_invoice_no)} className="ml-1.5 text-orange-400 hover:text-orange-300" title="Correct this invoice (admin)" data-testid={`edit-invoice-${item.id}`}>
                                    <Pencil size={11} className="inline" />
                                  </button>
                                )}
                              </>
                            )}
                          </td>
                          <td className="py-3 px-2 text-right space-x-1 whitespace-nowrap">
                            {tab === 'Awaiting Dispatch' && pending > 0 && (
                              <Button size="sm" className="bg-pink-600 hover:bg-pink-700 h-7 px-3 text-xs" onClick={() => openDispatch(item)} data-testid={`dispatch-${item.id}`}>
                                <Truck size={12} className="mr-1" /> Dispatch
                              </Button>
                            )}
                            {/* Legacy item (no lots) — fall back to single delivered button */}
                            {lots.length === 0 && item.fulfillment_status === 'Dispatched' && (
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 px-3 text-xs" onClick={() => setDeliverLot({ item, lot: null })} data-testid={`deliver-item-${item.id}`}>
                                <PackageCheck size={12} className="mr-1" /> Mark Delivered
                              </Button>
                            )}
                            {/* Resend last email (legacy items without lots) */}
                            {lots.length === 0 && (item.fulfillment_status === 'Dispatched' || item.fulfillment_status === 'Delivered') && (
                              <Button size="sm" variant="ghost" className={`h-7 px-2 text-xs ${isDark ? 'text-white/60 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'}`} onClick={() => handleResendItem(item.id)} title="Resend last email" data-testid={`resend-item-${item.id}`}>
                                <Mail size={12} className="mr-1" /> Resend
                              </Button>
                            )}
                            {/* Admin-only revert (Cycle 5 #5): un-dispatch / un-push if a lot went out by mistake */}
                            {user?.role === 'admin' && ['Dispatched', 'Partially Dispatched', 'Awaiting Dispatch', 'Pushed to Logistics'].includes(item.fulfillment_status) && (
                              <Button
                                size="sm" variant="outline"
                                className={`h-7 px-2 text-xs border-orange-500/40 ${isDark ? 'text-orange-300 hover:bg-orange-500/10' : 'text-orange-600 hover:bg-orange-50'}`}
                                onClick={() => handleRevert(item)}
                                title="Revert one step (admin only)"
                                data-testid={`revert-item-${item.id}`}
                              >
                                ↩ Revert
                              </Button>
                            )}
                          </td>
                        </tr>

                        {isExp && lots.length > 0 && (
                          <tr className={isDark ? 'bg-white/[0.02]' : 'bg-gray-50/60'}>
                            <td></td>
                            <td colSpan={6} className="py-3 px-2">
                              <div className={`text-[10px] uppercase tracking-wider mb-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Shipment Lots</div>
                              <div className="space-y-1.5">
                                {lots.map(lot => (
                                  <div key={lot.id} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-[11px] ${isDark ? 'bg-white/5' : 'bg-white border border-gray-200'}`} data-testid={`lot-${lot.id}`}>
                                    <div className="flex items-center gap-3 flex-wrap">
                                      <span className={`font-semibold ${isDark ? 'text-white/80' : 'text-gray-800'}`}>#{lot.lot_number}</span>
                                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${lot.status === 'Delivered' ? 'bg-green-500/15 text-green-400' : 'bg-blue-500/15 text-blue-400'}`}>{lot.status}</span>
                                      <span className={isDark ? 'text-white/60' : 'text-gray-700'}>Qty: <b>{lot.quantity}</b></span>
                                      <span className={isDark ? 'text-white/40' : 'text-gray-500'}>
                                        {lot.dispatch_mode === 'courier' ? `${lot.courier_name} / ${lot.awb_no}` : `${lot.dispatch_mode === 'own' ? 'Own' : 'Delivery Person'}: ${lot.dispatch_person}`}
                                      </span>
                                      {lot.expected_delivery && <span className={isDark ? 'text-white/40' : 'text-gray-500'}>ETA: {lot.expected_delivery}</span>}
                                      {lot.pod_url && (
                                        <a href={fileLink(lot.pod_url)} target="_blank" rel="noreferrer" className="text-green-400 inline-flex items-center gap-1">POD <ExternalLink size={10} /></a>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 whitespace-nowrap">
                                      {lot.status === 'Dispatched' && (
                                        <Button size="sm" className="bg-green-600 hover:bg-green-700 h-6 px-2 text-[10px]" onClick={() => setDeliverLot({ item, lot })} data-testid={`deliver-lot-${lot.id}`}>
                                          <PackageCheck size={10} className="mr-1" /> Deliver
                                        </Button>
                                      )}
                                      <Button size="sm" variant="ghost" className={`h-6 px-2 text-[10px] ${isDark ? 'text-white/60 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'}`} onClick={() => handleResendLot(lot.id)} title="Resend email for this lot" data-testid={`resend-lot-${lot.id}`}>
                                        <Mail size={10} className="mr-1" /> Resend
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        )}
        {!loading && items.length < total && (
          <div className="flex justify-center pt-1">
            <Button variant="outline" onClick={() => fetchData(page + 1)} disabled={loadingMore}
              className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''} data-testid="logistics-load-more">
              {loadingMore ? 'Loading…' : `Load more (showing ${items.length} of ${total})`}
            </Button>
          </div>
        )}
        </>
      )}

      {/* Dispatch Dialog */}
      <Dialog open={!!dispatchItem_} onOpenChange={(v) => { if (!v) setDispatchItem_(null); }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`}>
          {dispatchItem_ && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>Dispatch — {dispatchItem_.item_name}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleDispatch} className="space-y-3 mt-2">
                <div className={`text-xs p-2 rounded ${isDark ? 'bg-white/5 text-white/60' : 'bg-gray-50 text-gray-600'}`}>
                  SO: <b>{dispatchItem_.so_number}</b> | Customer: <b>{dispatchItem_.order_info?.customer_name}</b><br/>
                  Quantity to dispatch (from Billing): <b className="text-pink-400">{dispatchItem_._pending}</b> units
                </div>

                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Dispatch By *</Label>
                  <div className="mt-1 grid grid-cols-3 gap-2">
                    {[{ v: 'own', l: 'Our Logistics' }, { v: 'delivery_person', l: 'Delivery Person' }, { v: 'courier', l: 'Courier' }].map(opt => (
                      <button key={opt.v} type="button" onClick={() => setDForm(f => ({ ...f, dispatch_mode: opt.v }))}
                        className={`px-2 py-2 rounded-lg text-xs font-medium transition-all border ${
                          dForm.dispatch_mode === opt.v
                            ? 'bg-pink-600 text-white border-pink-600'
                            : isDark ? 'bg-white/5 border-white/10 text-white/60' : 'bg-white border-gray-200 text-gray-600'
                        }`} data-testid={`dispatch-mode-${opt.v}`}>{opt.l}</button>
                    ))}
                  </div>
                </div>

                {dForm.dispatch_mode === 'own' && (
                  <div>
                    <Label className={isDark ? 'text-white/60' : ''}>Staff Name *</Label>
                    <Input className={`mt-1 ${inputCls}`} value={dForm.dispatch_person}
                      onChange={e => setDForm(f => ({ ...f, dispatch_person: e.target.value }))}
                      placeholder="e.g., Driver name" required data-testid="dispatch-staff" />
                  </div>
                )}

                {dForm.dispatch_mode === 'delivery_person' && (
                  <div>
                    <Label className={isDark ? 'text-white/60' : ''}>Delivery Person *</Label>
                    <div className="mt-1 flex gap-2">
                      <select className={`flex-1 h-9 rounded-lg text-sm px-3 border ${selectCls}`} value={dForm.dispatch_person} onChange={e => setDForm(f => ({ ...f, dispatch_person: e.target.value }))} required data-testid="dispatch-person-select">
                        <option value="">Select person</option>
                        {deliveryPersons.map(p => <option key={p.id} value={p.name}>{p.name}{p.phone ? ` (${p.phone})` : ''}</option>)}
                      </select>
                      <Button type="button" size="sm" onClick={() => setShowAddPerson(true)} className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="add-delivery-person">+</Button>
                    </div>
                  </div>
                )}

                {dForm.dispatch_mode === 'courier' && (
                  <>
                    <div>
                      <Label className={isDark ? 'text-white/60' : ''}>Courier / Transport *</Label>
                      <Input className={`mt-1 ${inputCls}`} value={dForm.courier_name}
                        onChange={e => setDForm(f => ({ ...f, courier_name: e.target.value }))}
                        placeholder="e.g., DTDC, BlueDart" required data-testid="dispatch-courier" />
                    </div>
                    <div>
                      <Label className={isDark ? 'text-white/60' : ''}>Tracking No *</Label>
                      <Input className={`mt-1 ${inputCls}`} value={dForm.awb_no}
                        onChange={e => setDForm(f => ({ ...f, awb_no: e.target.value }))} required data-testid="dispatch-awb" />
                    </div>
                  </>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className={isDark ? 'text-white/60' : ''}>Vehicle No</Label>
                    <Input className={`mt-1 ${inputCls}`} value={dForm.vehicle_no}
                      onChange={e => setDForm(f => ({ ...f, vehicle_no: e.target.value }))} />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-white/60' : ''}>Expected Delivery</Label>
                    <Input type="date" className={`mt-1 ${inputCls}`} value={dForm.expected_delivery}
                      onChange={e => setDForm(f => ({ ...f, expected_delivery: e.target.value }))} />
                  </div>
                </div>

                <EmailToggle checked={dForm.send_email} onChange={v => setDForm(f => ({ ...f, send_email: v }))} isDark={isDark} testId="dispatch-send-email" />
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDispatchItem_(null)} className={`flex-1 h-10 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}>Cancel</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 h-10 bg-pink-600 hover:bg-pink-700 disabled:opacity-50" data-testid="dispatch-submit">
                    {submitting ? 'Dispatching…' : 'Dispatch & Notify'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk (Bill-wise) Dispatch Dialog */}
      <Dialog open={!!bulkInvoice} onOpenChange={(v) => { if (!v) setBulkInvoice(null); }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="bulk-dispatch-dialog">
          {bulkInvoice && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>Dispatch invoice {bulkInvoice.invoice}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleBulkDispatch} className="space-y-3 mt-2">
                <div className={`text-xs p-2 rounded ${isDark ? 'bg-white/5 text-white/60' : 'bg-gray-50 text-gray-600'}`}>
                  Customer: <b>{bulkInvoice.customer || '-'}</b><br/>
                  Items: <b className="text-pink-400">{bulkInvoice.items.length}</b> · Units: <b className="text-pink-400">{bulkInvoice.items.reduce((s, it) => s + (it._pending || 0), 0)}</b>
                  <div className="mt-1 max-h-20 overflow-y-auto">
                    {bulkInvoice.items.map(it => (
                      <div key={it.id} className="text-[10px] truncate">• {it.item_name} ({it._pending})</div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Dispatch By *</Label>
                  <div className="mt-1 grid grid-cols-3 gap-2">
                    {[{ v: 'own', l: 'Our Logistics' }, { v: 'delivery_person', l: 'Delivery Person' }, { v: 'courier', l: 'Courier' }].map(opt => (
                      <button key={opt.v} type="button" onClick={() => setBulkForm(f => ({ ...f, dispatch_mode: opt.v }))}
                        className={`px-2 py-2 rounded-lg text-xs font-medium transition-all border ${
                          bulkForm.dispatch_mode === opt.v
                            ? 'bg-pink-600 text-white border-pink-600'
                            : isDark ? 'bg-white/5 border-white/10 text-white/60' : 'bg-white border-gray-200 text-gray-600'
                        }`} data-testid={`bulk-dispatch-mode-${opt.v}`}>{opt.l}</button>
                    ))}
                  </div>
                </div>
                {bulkForm.dispatch_mode === 'own' && (
                  <div>
                    <Label className={isDark ? 'text-white/60' : ''}>Staff Name *</Label>
                    <Input className={`mt-1 ${inputCls}`} value={bulkForm.dispatch_person}
                      onChange={e => setBulkForm(f => ({ ...f, dispatch_person: e.target.value }))} placeholder="e.g., Driver name" required data-testid="bulk-dispatch-staff" />
                  </div>
                )}
                {bulkForm.dispatch_mode === 'delivery_person' && (
                  <div>
                    <Label className={isDark ? 'text-white/60' : ''}>Delivery Person *</Label>
                    <select className={`mt-1 w-full h-9 rounded-lg text-sm px-3 border ${selectCls}`} value={bulkForm.dispatch_person} onChange={e => setBulkForm(f => ({ ...f, dispatch_person: e.target.value }))} required data-testid="bulk-dispatch-person-select">
                      <option value="">Select person</option>
                      {deliveryPersons.map(p => <option key={p.id} value={p.name}>{p.name}{p.phone ? ` (${p.phone})` : ''}</option>)}
                    </select>
                  </div>
                )}
                {bulkForm.dispatch_mode === 'courier' && (
                  <>
                    <div>
                      <Label className={isDark ? 'text-white/60' : ''}>Courier / Transport *</Label>
                      <Input className={`mt-1 ${inputCls}`} value={bulkForm.courier_name}
                        onChange={e => setBulkForm(f => ({ ...f, courier_name: e.target.value }))} placeholder="e.g., DTDC" required data-testid="bulk-dispatch-courier" />
                    </div>
                    <div>
                      <Label className={isDark ? 'text-white/60' : ''}>Tracking No *</Label>
                      <Input className={`mt-1 ${inputCls}`} value={bulkForm.awb_no}
                        onChange={e => setBulkForm(f => ({ ...f, awb_no: e.target.value }))} required data-testid="bulk-dispatch-awb" />
                    </div>
                  </>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className={isDark ? 'text-white/60' : ''}>Vehicle No</Label>
                    <Input className={`mt-1 ${inputCls}`} value={bulkForm.vehicle_no}
                      onChange={e => setBulkForm(f => ({ ...f, vehicle_no: e.target.value }))} />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-white/60' : ''}>Expected Delivery</Label>
                    <Input type="date" className={`mt-1 ${inputCls}`} value={bulkForm.expected_delivery}
                      onChange={e => setBulkForm(f => ({ ...f, expected_delivery: e.target.value }))} />
                  </div>
                </div>
                <EmailToggle checked={bulkForm.send_email} onChange={v => setBulkForm(f => ({ ...f, send_email: v }))} isDark={isDark} testId="bulk-dispatch-send-email" />
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setBulkInvoice(null)} className={`flex-1 h-10 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}>Cancel</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 h-10 bg-pink-600 hover:bg-pink-700 disabled:opacity-50" data-testid="bulk-dispatch-submit">
                    {submitting ? 'Dispatching…' : 'Dispatch entire invoice'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk (Bill-wise) Deliver Dialog */}
      <Dialog open={!!bulkDeliver} onOpenChange={(v) => { if (!v) setBulkDeliver(null); }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="bulk-deliver-dialog">
          {bulkDeliver && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>Deliver invoice {bulkDeliver.invoice}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleBulkDeliver} className="space-y-3 mt-2">
                <div className={`text-xs p-2 rounded ${isDark ? 'bg-white/5 text-white/60' : 'bg-gray-50 text-gray-600'}`}>
                  Customer: <b>{bulkDeliver.customer || '-'}</b><br/>
                  Items: <b className="text-green-400">{bulkDeliver.items.length}</b> · Lots: <b className="text-green-400">{bulkDeliver.totalLots}</b>
                  <div className="mt-1 max-h-20 overflow-y-auto">
                    {bulkDeliver.items.map(it => (
                      <div key={it.id} className="text-[10px] truncate">• {it.item_name} ({(it._lots || []).reduce((s, l) => s + (l.quantity || 0), 0)} units)</div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Receiver Name</Label>
                  <Input className={`mt-1 ${inputCls}`} value={bulkDeliverForm.receiver_name}
                    onChange={e => setBulkDeliverForm(f => ({ ...f, receiver_name: e.target.value }))} placeholder="Person who received the parcel" data-testid="bulk-deliver-receiver" />
                </div>
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Proof of Delivery (POD) *</Label>
                  <div className="mt-1 flex items-center gap-3"
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files; if (f?.length) handleUpload({ target: { files: f } }, setBulkDeliverForm, 'pod_url'); }}
                    data-testid="bulk-deliver-pod-dropzone">
                    <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 rounded-lg border-2 border-dashed cursor-pointer text-sm ${isDark ? 'border-white/20 text-white/60 hover:bg-white/5' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                      <Upload size={16} /> {bulkDeliverForm.pod_url ? 'Uploaded' : 'Upload or drag & drop POD here'}
                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => handleUpload(e, setBulkDeliverForm, 'pod_url')} data-testid="bulk-deliver-pod-upload" />
                    </label>
                    {bulkDeliverForm.pod_url && <span className="text-green-400 text-xs flex items-center gap-1"><Check size={12} /> Uploaded</span>}
                  </div>
                  <p className={`mt-1 text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>One POD will be attached to every item-lot of this invoice. A single combined email goes to the customer.</p>
                </div>
                <EmailToggle checked={bulkDeliverForm.send_email} onChange={v => setBulkDeliverForm(f => ({ ...f, send_email: v }))} isDark={isDark} testId="bulk-deliver-send-email" />
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setBulkDeliver(null)} className={`flex-1 h-10 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}>Cancel</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 h-10 bg-green-600 hover:bg-green-700 disabled:opacity-50" data-testid="bulk-deliver-submit">
                    {submitting ? 'Delivering…' : 'Deliver entire invoice'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Deliver (lot or legacy item) Dialog */}
      <Dialog open={!!deliverLot} onOpenChange={(v) => { if (!v) setDeliverLot(null); }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`}>
          {deliverLot && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>
                  Mark Delivered — {deliverLot.item.item_name}
                  {deliverLot.lot && <span className={`ml-2 text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Lot #{deliverLot.lot.lot_number} • {deliverLot.lot.quantity} units</span>}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleDeliverLot} className="space-y-3 mt-2">
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Receiver Name</Label>
                  <Input className={`mt-1 ${inputCls}`} value={pForm.receiver_name}
                    onChange={e => setPForm(f => ({ ...f, receiver_name: e.target.value }))} placeholder="Person who received the parcel" />
                </div>
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Proof of Delivery (POD) *</Label>
                  <div className="mt-1 flex items-center gap-3"
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files; if (f?.length) handleUpload({ target: { files: f } }, setPForm, 'pod_url'); }}
                    data-testid="deliver-pod-dropzone">
                    <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 rounded-lg border-2 border-dashed cursor-pointer text-sm ${isDark ? 'border-white/20 text-white/60 hover:bg-white/5' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                      <Upload size={16} /> {pForm.pod_url ? 'Uploaded' : 'Upload or drag & drop POD here'}
                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => handleUpload(e, setPForm, 'pod_url')} data-testid="pod-upload" />
                    </label>
                    {pForm.pod_url && <span className="text-green-400 text-xs flex items-center gap-1"><Check size={12} /> Uploaded</span>}
                  </div>
                </div>
                <EmailToggle checked={pForm.send_email} onChange={v => setPForm(f => ({ ...f, send_email: v }))} isDark={isDark} testId="deliver-send-email" />
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDeliverLot(null)} className={`flex-1 h-10 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}>Cancel</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 h-10 bg-green-600 hover:bg-green-700 disabled:opacity-50" data-testid="deliver-submit">
                    {submitting ? 'Confirming…' : 'Confirm & Notify'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Invoice Correction Dialog */}
      <Dialog open={!!editInv} onOpenChange={(v) => { if (!v) setEditInv(null); }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="edit-invoice-dialog">
          {editInv && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>Correct invoice {editInv.old_no}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEditInvoice} className="space-y-3 mt-2">
                <div className={`text-xs p-2 rounded ${isDark ? 'bg-orange-500/10 text-orange-200' : 'bg-orange-50 text-orange-700'}`}>
                  Admin only — updates the invoice number / date / PDF on every item and dispatch lot linked to <b>{editInv.old_no}</b>. Change only what needs fixing.
                </div>
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Correct Invoice No</Label>
                  <Input className={`mt-1 ${inputCls}`} value={editInv.new_no}
                    onChange={e => setEditInv(f => ({ ...f, new_no: e.target.value }))} data-testid="edit-invoice-new-no" />
                </div>
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Correct Invoice Date (optional)</Label>
                  <Input type="date" className={`mt-1 ${inputCls}`} value={editInv.invoice_date}
                    onChange={e => setEditInv(f => ({ ...f, invoice_date: e.target.value }))} data-testid="edit-invoice-date" />
                </div>
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Replace Invoice PDF (optional)</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm ${isDark ? 'border-white/20 text-white/60 hover:bg-white/5' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                      <Upload size={16} /> {editInv.invoice_pdf_url ? 'Uploaded' : 'Upload corrected PDF'}
                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => handleUpload(e, setEditInv, 'invoice_pdf_url')} data-testid="edit-invoice-pdf-upload" />
                    </label>
                    {editInv.invoice_pdf_url && <span className="text-green-400 text-xs flex items-center gap-1"><Check size={12} /> Uploaded</span>}
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setEditInv(null)} className={`flex-1 h-10 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}>Cancel</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 h-10 bg-orange-600 hover:bg-orange-700 disabled:opacity-50" data-testid="edit-invoice-submit">
                    {submitting ? 'Saving…' : 'Save correction'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Delivery Person Dialog */}
      <Dialog open={showAddPerson} onOpenChange={setShowAddPerson}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`}>
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : ''}>Delivery Persons</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {deliveryPersons.length > 0 && (
              <div className={`max-h-40 overflow-y-auto rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                {deliveryPersons.map(p => (
                  <div key={p.id} className={`flex items-center justify-between px-3 py-2 text-xs border-b last:border-b-0 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                    <div>
                      <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{p.name}</div>
                      <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{p.phone || ''} {p.vehicle ? `• ${p.vehicle}` : ''}</div>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/10" onClick={() => handleDeletePerson(p)} title="Delete" data-testid={`delete-person-${p.id}`}>
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className={`pt-2 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <p className={`text-[10px] uppercase tracking-wider mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Add new person</p>
              <Input className={inputCls} placeholder="Name *" value={newPersonForm.name} onChange={e => setNewPersonForm(p => ({ ...p, name: e.target.value }))} data-testid="new-person-name" />
              <Input className={`mt-2 ${inputCls}`} placeholder="Phone" value={newPersonForm.phone} onChange={e => setNewPersonForm(p => ({ ...p, phone: e.target.value }))} />
              <Input className={`mt-2 ${inputCls}`} placeholder="Vehicle (optional)" value={newPersonForm.vehicle} onChange={e => setNewPersonForm(p => ({ ...p, vehicle: e.target.value }))} />
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setShowAddPerson(false)} className={`flex-1 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}>Close</Button>
              <Button type="button" onClick={handleAddPerson} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-new-person">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlowLogistics;
